import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  Container,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { publicRequest } from "../../api/client";
import { PublicProject } from "../../types/projects";
import { formatDate, formatHours, formatMoney } from "../../utils/format";
import { downloadHoursPdf } from "../../utils/hoursPdf";

const HoursSummary: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<PublicProject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPending, setShowPending] = useState(true);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    publicRequest<PublicProject>(`/api/public/projects/${projectId}`)
      .then(setProject)
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load hours");
      });
  }, [projectId]);

  const visibleEntries = useMemo(() => {
    if (!project) {
      return [];
    }
    return showPending
      ? project.entries.filter((entry) => !entry.paid)
      : project.entries;
  }, [project, showPending]);

  const hours = project
    ? showPending
      ? project.totals.pendingHours
      : project.totals.totalHours
    : 0;
  const amount = project
    ? showPending
      ? project.totals.pendingAmount
      : project.totals.totalAmount
    : 0;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", py: 8 }}>
      <Container maxWidth="md">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!project && !error ? (
          <Typography>Loading hours…</Typography>
        ) : project ? (
          <>
            <Typography variant="h2">{project.name}</Typography>
            <Typography color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
              Hours summary
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showPending}
                    onChange={(event) => setShowPending(event.target.checked)}
                  />
                }
                label={showPending ? "Pending hours" : "Total hours"}
              />
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() =>
                  downloadHoursPdf(project, visibleEntries, showPending)
                }
              >
                Download PDF
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 4,
              }}
            >
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {showPending ? "Pending hours" : "Total hours"}
                </Typography>
                <Typography variant="h3" sx={{ mb: 0 }}>
                  {formatHours(hours)}
                </Typography>
              </Paper>
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {showPending ? "Pending amount" : "Total amount"}
                </Typography>
                <Typography variant="h3" sx={{ mb: 0 }}>
                  {formatMoney(amount)}
                </Typography>
              </Paper>
            </Box>

            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        {showPending
                          ? "No pending hours."
                          : "No hours logged yet."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{formatDate(entry.date)}</TableCell>
                        <TableCell align="right">
                          {formatHours(entry.hours)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(entry.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        ) : null}
      </Container>
    </Box>
  );
};

export default HoursSummary;
